import { Checkbox, FormControlLabel } from '@material-ui/core';
import { TreeView, TreeItem, TreeViewProps } from '@material-ui/lab';
import React from 'react';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

export interface RenderTree {
    id: number
    name: string
    children?: RenderTree[]
}

export type CheckTreeViewProps = TreeViewProps & {
    data: RenderTree
    currentSelected: number[]
}

export function CheckTreeView ({ data, currentSelected, ...props }: CheckTreeViewProps) {
    const [selected, setSelected] = React.useState<number[]>(currentSelected);
  
    const selectedSet = React.useMemo(() => new Set(selected), [selected]);
  
    const getAllChild = React.useCallback((childNode: RenderTree | null, collectedNodes: any[] = []) => {
        if (childNode === null) return collectedNodes;
    
        collectedNodes.push(childNode.id);
    
        if (Array.isArray(childNode.children)) {
            for (const node of childNode.children) {
                getAllChild(node, collectedNodes);
            }
        }
    
        return collectedNodes;
      }, [])

    const goThroughAllNodes = React.useCallback((nodes: RenderTree, map: Record<string, any> = {}) => {
        if (!nodes.children) {
            return null;
        }
    
        map[nodes.id] = getAllChild(nodes).splice(1);
    
        for (let childNode of nodes.children) {
            goThroughAllNodes(childNode, map);
        }
    
        return map;
    }, [getAllChild])

    const parentMap = React.useMemo(() => {
      return goThroughAllNodes(data);
    }, [data, goThroughAllNodes]);
  
  
    const getChildById = React.useCallback((nodes: RenderTree, id: number) => {
        let array: string[] = [];
        let path: number[] = [];
    
        // recursive DFS
        function getNodeById(node: RenderTree, id: number, parentsPath: number[]) : any {
            let result = null;
        
            if (node.id === id) {
                return node;
            } else if (Array.isArray(node.children)) {
                for (let childNode of node.children) {
                    result = getNodeById(childNode, id, parentsPath);
            
                    if (!!result) {
                        parentsPath.push(node.id);
                        return result;
                    }
                }
        
                return result;
            }
        
            return result;
        }
    
        const nodeToToggle = getNodeById(nodes, id, path);
    
        return { childNodesToToggle: getAllChild(nodeToToggle, array), path };
      }, [getAllChild])
  
    const getOnChange = React.useCallback((checked: boolean, nodes: RenderTree) => {
        const { childNodesToToggle, path } = getChildById(data, nodes.id);
    
        let array = checked
          ? [...selected, ...childNodesToToggle]
          : selected
              .filter((value) => !childNodesToToggle.includes(value))
              .filter((value) => !path.includes(value));
    
        array = array.filter((v, i) => array.indexOf(v) === i);
    
        setSelected(array);
    }, [data, getChildById, selected])
  
    const renderTree = React.useCallback((nodes: RenderTree) => {
        const allSelectedChildren = parentMap !== null && parentMap[nodes.id]?.every((childNodeId: number) => selectedSet.has(childNodeId));
        const checked = selectedSet.has(nodes.id) || allSelectedChildren || false;
        const indeterminate = parentMap !== null && (parentMap[nodes.id]?.some((childNodeId: number) => selectedSet.has(childNodeId)) || false);
    
        if (allSelectedChildren && !selectedSet.has(nodes.id)) { 
            setSelected([...selected, nodes.id]);
        }

        return (
            <TreeItem
                key={nodes.id}
                nodeId={nodes.id.toString()}
                label={
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={checked}
                                indeterminate={!checked && indeterminate}
                                onChange={(event) =>
                                    getOnChange(event.currentTarget.checked, nodes)
                                }
                                onClick={(e) => e.stopPropagation()}
                            />
                        }
                        label={<>{nodes.name}</>}
                        key={nodes.id}
                    />
                }
            >
                {
                    Array.isArray(nodes.children) && nodes.children.map((node) => renderTree(node))
                }
            </TreeItem>
        )        
    }, [getOnChange, parentMap, selected, selectedSet]);

    return (
        <TreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            {...props}
        >
            {renderTree(data)}
        </TreeView>
    )
}